import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { apiURL } from '../utilities/URLs'
import { account } from '../classes/Account'
import { refreshAccessToken } from '../utilities/extensions'

export default function ACHBanner({ applicationId: propApplicationId }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMissingACH, setIsMissingACH] = useState(false)

  // Extract applicationId from URL path if not passed as prop
  const applicationId = propApplicationId || extractApplicationIdFromPath(location.pathname)

  useEffect(() => {
    if (!applicationId) return

    const checkACHStatus = async () => {
      try {
        const res = await fetch(`${apiURL}/api/applications/${applicationId}/ach-status`, {
          headers: { 'Authorization': account.accessToken }
        })
        const data = await res.json()

        if (data.response?.error) {
          if (data.response.error === 'Failed To Authenticate Token.') {
            const refreshed = await refreshAccessToken()
            if (refreshed && refreshed.length > 0) return checkACHStatus()
          }
          return
        }

        setIsMissingACH(data.response.isMissingACH)
      } catch (err) {
        console.error('ACH status check failed:', err)
      }
    }

    checkACHStatus()
  }, [applicationId])

  if (!isMissingACH) return null

  return (
    <div className='w-full flex items-center justify-between bg-red-50 dark:bg-red-900/20 shadow-sm border-b border-red-200 dark:border-red-600/30 px-6 py-3 transition-colors duration-300 z-50'>
      <div className='flex items-center gap-3'>
        <div className='flex items-center justify-center bg-red-500 dark:bg-red-500/30 text-white dark:text-red-400 rounded-full w-8 h-8 shadow-sm'>
          <AlertTriangle size={16} />
        </div>
        <div className='flex flex-col'>
          <span className='text-xs font-bold text-red-900 dark:text-red-400 uppercase tracking-wider'>ACH Setup Required</span>
          <span className='text-sm font-medium text-red-800 dark:text-red-500/90'>Your bank account and ACH agreement are not yet set up. Please complete setup to enable automatic payments.</span>
        </div>
      </div>

      <button
        onClick={() => navigate(`/applications/${applicationId}/bank-verification?update=1`)}
        className='flex items-center gap-2 px-4 py-1.5 bg-red-900/10 hover:bg-red-900/20 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-900 dark:text-red-400 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 border border-red-900/10 dark:border-red-500/20 backdrop-blur-sm whitespace-nowrap cursor-pointer'
      >
        Set Up Now
      </button>
    </div>
  )
}

/**
 * Extracts applicationId from URL paths like /applications/:applicationId/...
 * @param {String} pathname
 * @returns {String}
 */
function extractApplicationIdFromPath(pathname) {
  const match = pathname.match(/\/applications\/([^/]+)/)
  return match ? match[1] : ''
}
